import { EventBus } from './eventBus'
import { useChatStore } from '../stores/chatStore'
import CacheManager from './cache/CacheManager'

import http from '../http-common'
import { metadataFile, resolveChatFile, mediaScope } from './chat/ChatMedia'

// Simple MIME type to extension mapping (browser-compatible)
const mimeToExtension = (mimeType: string): string => {
  const mimeMap: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/ogg': 'ogv',
    'audio/mpeg': 'mp3',
    'audio/ogg': 'ogg',
    'audio/wav': 'wav',
    'audio/webm': 'weba',
    'application/pdf': 'pdf',
    'application/zip': 'zip',
    'application/json': 'json',
    'text/plain': 'txt',
    'text/html': 'html',
    'text/css': 'css',
    'text/javascript': 'js',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  }
  return mimeMap[mimeType.split(';', 1)[0].trim().toLowerCase()] || 'bin'
}

// Rotas de proxy exigem a base da API e os headers do cliente HTTP.
// Somente URLs diretamente reproduzíveis podem ser atribuídas ao player.
const resolveDirectMediaUrl = (file: any): string | null => {
  const url = file.file_url || file.url
  if (typeof url !== 'string' || !url) return null
  try {
    const pathname = new URL(url, 'https://media.invalid/').pathname
    if (/\/integrations\/media\/[^/]+\/?$/.test(pathname)) return null
  } catch {
    return null
  }
  return url
}

// Corrige tipagem para permitir indexação por string
const sockets: Record<string, WebSocket> = {} // Objeto para gerenciar múltiplas conexões WebSocket
// Mapa para controlar tentativas de reconexão por chave
const reconnectAttempts: Record<string, number> = {}
const reconnectTimeouts: Record<string, ReturnType<typeof setTimeout>> = {}
const manualDisconnects: Set<string> = new Set()

// ✅ ADICIONADO: Cache para preservar dados originais dos arquivos enviados
const temporaryFileCache: Map<string, any[]> = new Map()

// Track active object URLs so components can revoke them when discarding previews
const activeObjectUrls: Set<string> = new Set()

const typingCleanupTimers: Record<string, ReturnType<typeof setTimeout>> = {}
// Throttle map to avoid spamming room refresh on bursty status_update events
const roomRefreshThrottle: Record<string, number> = {}
const DEFAULT_TYPING_TIMEOUT_MS = 4000
const lastUnreadUpdateFromChat: Record<string, number> = {}
const readReceiptThrottleMap: Record<string, number> = {}
const READ_RECEIPT_THROTTLE_MS = 2000
const NOTIFICATION_FALLBACK_WINDOW_MS = 1500
// Track read_receipts sent to avoid duplicates and ensure we only send with a valid WAM ID
const sentReadReceipts: Record<string, number> = {}
const READ_RECEIPT_MEMORY_TTL_MS = 5 * 60 * 1000 // 5 minutes

function cleanupSentReadReceipts() {
  const now = Date.now()
  Object.keys(sentReadReceipts).forEach((key) => {
    if (now - sentReadReceipts[key] > READ_RECEIPT_MEMORY_TTL_MS) {
      delete sentReadReceipts[key]
    }
  })
}

export function extractWamid(input: any): string | null {
  if (!input || typeof input !== 'object') return null
  const candidates = [(input as any).wamid, (input as any).message_id, (input as any).external_id, (input as any).wa_id]
  for (const c of candidates) {
    // Restringe a validação para IDs que iniciem com o prefixo esperado
    if (typeof c === 'string' && c.startsWith('wamid.')) {
      return c
    }
  }
  return null
}

function resolveExternalId(payload: any): string | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const directCandidates = [
    (payload as any).external_id,
    (payload as any).message_external_id,
    (payload as any).status_external_id,
    (payload as any).communication_external_id,
  ]

  for (const candidate of directCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate
    }
  }

  const statusObj = (payload as any).status
  if (statusObj && typeof statusObj === 'object') {
    const statusId = statusObj.id || statusObj.message_id || statusObj.status_id
    if (typeof statusId === 'string' && statusId.trim()) {
      return statusId
    }
  }

  const fromMessage = extractWamid((payload as any).message || payload)
  if (fromMessage) {
    return fromMessage
  }

  return null
}

function clearTypingTimer(roomKey: string) {
  const timer = typingCleanupTimers[roomKey]
  if (timer) {
    clearTimeout(timer)
    delete typingCleanupTimers[roomKey]
  }
}

function scheduleTypingClear(roomId: string, userId: string) {
  const key = `${roomId}:${userId}`
  clearTypingTimer(key)

  typingCleanupTimers[key] = setTimeout(() => {
    try {
      const store = useChatStore()
      store.setTypingStatus(roomId, userId, false)
    } catch (error) {
      console.warn('[WebSocket] Erro ao limpar indicador de digitação', { roomId, userId, error })
    } finally {
      delete typingCleanupTimers[key]
    }
  }, DEFAULT_TYPING_TIMEOUT_MS)
}

function handleTypingIndicator(chatStoreInstance: ReturnType<typeof useChatStore>, payload: Record<string, any>) {
  if (!payload || typeof payload !== 'object') {
    console.warn('[WebSocket] Evento typing inválido', payload)
    return
  }

  const rawRoomId =
    payload.roomId ??
    payload.room_id ??
    payload.lead_id ??
    payload.lead ??
    payload.room ??
    (payload.message && (payload.message.roomId || payload.message.lead))

  const rawUserId = payload.user_id ?? payload.user ?? payload.company_user_id ?? payload.id

  let isTyping: boolean | null = null

  if (typeof payload.is_typing === 'boolean') {
    isTyping = payload.is_typing
  } else if (typeof payload.status === 'string') {
    const normalized = payload.status.trim().toLowerCase()
    if (normalized === 'start') isTyping = true
    if (normalized === 'stop') isTyping = false
  }

  if (!rawRoomId || rawUserId === undefined || isTyping === null) {
    console.warn('[WebSocket] Evento typing incompleto', { rawRoomId, rawUserId, isTyping, payload })
    return
  }

  const roomId = String(rawRoomId)
  const userId = String(rawUserId)

  if (!roomId || roomId === 'undefined' || roomId === 'null') {
    console.warn('[WebSocket] Evento typing com roomId inválido', { roomId, payload })
    return
  }

  if (!userId || userId === 'undefined' || userId === 'null') {
    console.warn('[WebSocket] Evento typing com userId inválido', { userId, payload })
    return
  }

  try {
    chatStoreInstance.setTypingStatus(roomId, userId, isTyping)
  } catch (error) {
    console.warn('[WebSocket] Erro ao atualizar indicador de digitação', { roomId, userId, error })
  }

  if (isTyping) {
    scheduleTypingClear(roomId, userId)
  } else {
    clearTypingTimer(`${roomId}:${userId}`)
  }
}

export function revokeObjectUrl(url?: string) {
  if (!url) return
  try {
    URL.revokeObjectURL(url)
    activeObjectUrls.delete(url)
    console.debug('[WebSocket] Revoked object URL:', url)
  } catch (err) {
    console.warn('[WebSocket] Failed to revoke object URL:', url, err)
  }
}

/**
 * Armazena dados originais dos arquivos para recuperar quando a mensagem retornar
 * @param temporaryId - ID temporário da mensagem
 * @param files - Array com dados originais dos arquivos
 */
export function cacheTemporaryFiles(temporaryId: string, files: any[]): void {
  temporaryFileCache.set(temporaryId, files)

  // Limpar cache após 30 segundos para evitar vazamentos de memória
  setTimeout(() => {
    temporaryFileCache.delete(temporaryId)
  }, 30000)
}

export function connectWebSocket(url: string, key: string) {
  const wsBase = (import.meta as any).env.VITE_WEBSOCKET_URL

  // WebSocket auth: backend expects a token in the querystring (no headers in WS).
  // If there is no token, don't attempt connection (avoids backend denial + reconnect loops).
  const token = localStorage.getItem('token')
  if (!token) {
    console.warn(`[WebSocket] ⚠️ Token ausente; conexão não será estabelecida: ${key}`)
    return null
  }

  const cmpId = localStorage.getItem('cmpId')

  let wsUrl = `${wsBase}${url}`
  try {
    const full = new URL(url, wsBase)
    // Only set if not already present
    if (!full.searchParams.has('token')) {
      full.searchParams.set('token', token)
    }
    if (cmpId && cmpId !== 'null' && cmpId !== 'undefined' && !full.searchParams.has('company')) {
      full.searchParams.set('company', cmpId)
    }
    wsUrl = full.toString()
  } catch (e) {
    // Fallback: append query params manually.
    const joiner = wsUrl.includes('?') ? '&' : '?'
    wsUrl = `${wsUrl}${joiner}token=${encodeURIComponent(token)}`
    if (cmpId && cmpId !== 'null' && cmpId !== 'undefined') {
      wsUrl = `${wsUrl}&company=${encodeURIComponent(cmpId)}`
    }
  }

  const pendingTimeout = reconnectTimeouts[key]
  if (pendingTimeout) {
    clearTimeout(pendingTimeout)
    delete reconnectTimeouts[key]
  }

  if (sockets[key]) {
    const currentState = sockets[key].readyState

    if (currentState === WebSocket.OPEN) {
      console.debug(`[WebSocket] ✅ Reutilizando conexão ativa: "${key}"`)
      return sockets[key] // Retorna o socket, sem warning
    }

    if (currentState === WebSocket.CONNECTING) {
      console.debug(`[WebSocket] ⏳ Aguardando conexão em progresso: "${key}"`)
      return sockets[key] // Aguarda conexão atual, sem warning
    }

    // Se está fechando ou fechado, força limpeza
    console.debug(`[WebSocket] 🧹 Limpando conexão anterior "${key}" (estado: ${getStateDescription(currentState)})`)
    disconnectWebSocket(key)
  }

  let attempts = reconnectAttempts[key] || 0
  const socket = new WebSocket(wsUrl)

  // ✅ Adicionar timestamp de criação para tracking
  ;(socket as any)._connectedAt = Date.now()

  socket.onopen = () => {
    console.log(`[WebSocket] 🟢 Conectado: ${key}`)
    reconnectAttempts[key] = 0 // Reset ao conectar
    ;(socket as any)._lastActivity = Date.now()
    manualDisconnects.delete(key)
  }

  socket.onmessage = async (event) => {
    const chatStore = useChatStore()

    ;(socket as any)._lastActivity = Date.now()

    let data
    try {
      data = JSON.parse(event.data)
      console.log('[WebSocket] Mensagem recebida:', data)
    } catch (error) {
      console.error('[WebSocket] Erro ao fazer parse da mensagem:', error, 'Data:', event.data)
      return
    }

    // Multi-tenant filtering: Ignore messages from other companies if company ID is present
    const currentCompanyId = localStorage.getItem('cmpId')
    const messageCompanyId = data.company || (data.message && data.message.company) || (data.room && data.room.company)

    if (messageCompanyId && currentCompanyId && String(messageCompanyId) !== String(currentCompanyId)) {
      console.log('[WebSocket] Ignorando mensagem de outra empresa:', {
        type: data.type,
        messageCompanyId,
        currentCompanyId,
      })
      return
    }

    // Some providers may send bare status update objects without a type
    // Normalize to a synthetic status_update when possible
    if (!data.type && data.updating === true && (data.id || data.message_id) && (data.roomId || data.room_id)) {
      data.type = 'status_update'
      data.external_status = data.external_status || data.status || data.state
    }

    switch (data.type) {
      case 'chat_message':
        {
          console.debug('[WebSocket] chat_message payload:', data.message)

          if (!data.message) {
            console.error('[WebSocket] Mensagem sem dados:', data)
            return
          }

          // Handle message updates and deletions
          if (data.message.updating) {
            const roomId = String(data.message.roomId || data.message.lead_id || data.message.lead)
            const messageId = data.message.id || data.message._id
            const externalId = resolveExternalId(data.message)

            if (data.status === 'deleted') {
              chatStore.removeMessage(roomId, messageId, {
                externalId,
                temporaryId: data.message.temporary_id,
              })
              break
            }

            const updateData = {
              roomId: roomId,
              messageId: messageId,
              temporaryId: data.message.temporary_id,
              externalStatus: data.message.external_status,
              externalId,
            }

            // Check if message exists; if not, request a refresh (WhatsApp App echo)
            let hasMessage = false
            try {
              const list = chatStore.messagesForRoom(roomId) || []
              hasMessage = list.some(
                (m: any) => String(m._id) === String(messageId) || String(m.id) === String(messageId),
              )
            } catch (e) {
              hasMessage = false
            }

            chatStore.updateExternalStatus(updateData)

            if (!hasMessage) {
              const now = Date.now()
              const last = roomRefreshThrottle[roomId] || 0
              if (now - last > 800) {
                roomRefreshThrottle[roomId] = now
                EventBus.emit('chat:refresh-room', { roomId })
              }
            }
            break
          }

          // Lidando com reações que recebemos
          const isExplicitReaction = data.message.type === 'reaction' || data.message.reacting === true
          const isImplicitReaction =
            !isExplicitReaction &&
            !!data.message.reaction &&
            !data.message.content &&
            (!!data.message.communication || !!data.message.reply_to)

          if (isExplicitReaction || isImplicitReaction) {
            const roomId = data.message.roomId || data.message.lead_id || data.message.lead
            if (!roomId) {
              console.error('[WebSocket] updateReaction: sem roomId', data.message)
              break
            }

            const updateData = {
              roomId: String(roomId),
              // Preferir a mensagem alvo da reação (communication/reply_to)
              messageId: data.message.communication || data.message.reply_to || data.message.id || data.message._id,
              reaction: data.message.reaction,
            }

            chatStore.updateReaction(updateData)
            break
          }

          // Handle new messages
          if (data.message) {
            // Determine roomId from multiple possible sources
            let roomId = data.message.roomId || data.message.lead_id || data.message.lead || data.lead_id || data.lead

            if (!roomId) {
              console.error('[WebSocket] No roomId found in message:', data)
              return
            }

            roomId = String(roomId) // Ensure roomId is a string for consistent indexing

            // Generate consistent ID for vue-advanced-chat
            // Prioritize _id, id, then temporary_id, never generate random if any exists
            const messageId = data.message._id || data.message.id || data.message.temporary_id
            if (!messageId) {
              console.error('[WebSocket] No valid messageId found in message:', data)
              return
            }

            const senderId = data.message.direction !== 'outgoing' ? roomId : localStorage.getItem('cmpU')

            let files = []
            const temporaryId = data.message.temporary_id
            const isInstagramChannel =
              typeof (data.message as any).channel === 'string' &&
              (data.message as any).channel.toLowerCase().includes('instagram')

            if (temporaryId && temporaryFileCache.has(temporaryId)) {
              const cached = temporaryFileCache.get(temporaryId) || []
              // ✅ CORREÇÃO: Preservar dados originais, mas marcar como confirmado pelo backend
              files = cached.map((f: any) => ({
                ...f,
                local: false, // Não é mais local, mas preserva preview
                loading: false, // Não está mais carregando
                progress: 100, // Upload confirmado
                uploaded: true, // Confirmado pelo backend
                confirmed: true, // ✅ NOVO: Flag para identificar arquivo confirmado
                // ✅ CRÍTICO: Preservar preview original até download definitivo
                preview: f.preview, // Manter preview local
                url: f.url, // Manter URL local
              }))

              // ✅ IMPORTANTE: Não deletar cache ainda, deixar para limpeza posterior
              // temporaryFileCache.delete(temporaryId) - removido
              console.log('[WebSocket] Anexos recuperados do cache e marcados como confirmados:', files)
            }

            // Normalizar fontes de anexos (files[], attachments[], media_ids)
            const attachmentsArray = Array.isArray((data.message as any).attachments)
              ? (data.message as any).attachments
              : []
            const msgMediaIds = Array.isArray((data.message as any).media_ids) ? (data.message as any).media_ids : []
            const topMediaIds = Array.isArray((data as any).media_ids) ? (data as any).media_ids : []

            let filesSource: Array<any> | null = null

            if (data.message.files && Array.isArray(data.message.files) && data.message.files.length > 0) {
              filesSource = data.message.files
            } else if (attachmentsArray.length > 0 || msgMediaIds.length > 0 || topMediaIds.length > 0) {
              const fromAttachments = attachmentsArray.map((att: any) => {
                const id = att.id || att.media_id || att.mediaId || att.file_id || att.media || null
                const type = att.mime || att.mime_type || att.content_type || att.type || 'application/octet-stream'
                const name = att.file_name || att.filename || att.name || 'Anexo'
                const size = att.file_size || att.size || 0
                const file_url = att.file_url || att.url || null
                const thumb_url = att.thumb_url || att.thumbnail_url || null
                return { id, ref: att.ref, integration_id: att.integration_id, name, size, type, file_url, thumb_url }
              })

              const fromMsgMedia = msgMediaIds.map((id: any) => ({
                id,
                name: 'Anexo',
                size: 0,
                type: 'application/octet-stream',
                file_url: null,
                thumb_url: null,
              }))

              const fromTopMedia = topMediaIds.map((id: any) => ({
                id,
                name: 'Anexo',
                size: 0,
                type: 'application/octet-stream',
                file_url: null,
                thumb_url: null,
              }))

              filesSource = [...fromAttachments, ...fromMsgMedia, ...fromTopMedia]
            }

            if (filesSource && filesSource.length > 0) {
              console.debug('[WebSocket] Mensagem contém anexos (normalizados):', filesSource)
              console.debug('[WebSocket] Processando anexos:', filesSource.length)

              // ✅ MERGE: se houver cache temporário para essa mensagem, mesclar previews locais
              let mergedFilesSource = filesSource
              if (temporaryId && temporaryFileCache.has(temporaryId)) {
                try {
                  const cachedList = temporaryFileCache.get(temporaryId) || []
                  const cachedById = new Map<string, any>()
                  cachedList.forEach((c: any) => {
                    if (c && c.id !== undefined && c.id !== null) cachedById.set(String(c.id), c)
                  })

                  // Para cada arquivo vindo do servidor, se faltar URL/preview, preencher com cache
                  mergedFilesSource = filesSource.map((srv: any) => {
                    const idKey = String(srv.id ?? srv.media_id ?? srv.file_id ?? '')
                    const cached = cachedById.get(idKey)
                    if (!cached) return srv

                    // Priorizar URL do servidor se existir, mas preservar preview/local quando servidor não enviar url
                    const directUrl = srv.file_url || srv.url || null
                    return {
                      ...srv,
                      // se servidor não forneceu file_url, reaplicar url/preview do cache
                      file_url: directUrl || cached.url || cached.file_url || null,
                      thumb_url:
                        srv.thumb_url ||
                        srv.thumbnail_url ||
                        cached.preview ||
                        cached.thumb_url ||
                        cached.thumbnail_url ||
                        null,
                      // manter metadados do cache que ajudam a UI (size/type/name)
                      name: srv.name || cached.name,
                      size: srv.size || cached.size || 0,
                      type: srv.type || cached.type || 'application/octet-stream',
                    }
                  })

                  // Para Instagram, alinhar previews por índice para evitar duplicidades quando IDs diferem
                  if (isInstagramChannel && mergedFilesSource.length && cachedList.length) {
                    mergedFilesSource = mergedFilesSource.map((srv: any, idx: number) => {
                      const cached = cachedList[idx]
                      if (!cached) return srv

                      const directUrl = srv.file_url || srv.url || null
                      return {
                        ...srv,
                        file_url: directUrl || cached.url || cached.file_url || null,
                        thumb_url:
                          srv.thumb_url ||
                          srv.thumbnail_url ||
                          cached.preview ||
                          cached.thumb_url ||
                          cached.thumbnail_url ||
                          null,
                        name: srv.name || cached.name,
                        size: srv.size || cached.size || 0,
                        type: srv.type || cached.type || 'application/octet-stream',
                      }
                    })
                  }

                  const shouldAppendCachedExtras = !isInstagramChannel || mergedFilesSource.length === 0

                  // Adicionar quaisquer arquivos do cache que o servidor não retornou (ex.: preview local extra)
                  if (shouldAppendCachedExtras) {
                    cachedList.forEach((cf: any) => {
                      const exists = mergedFilesSource.some((m: any) => String(m.id) === String(cf.id))
                      if (!exists) {
                        mergedFilesSource.push({
                          id: cf.id,
                          name: cf.name || 'Arquivo',
                          size: cf.size || 0,
                          type: cf.type || 'application/octet-stream',
                          file_url: cf.url || cf.file_url || null,
                          thumb_url: cf.preview || null,
                        })
                      }
                    })
                  }

                  console.debug('[WebSocket] Mesclado filesSource com cache temporário:', mergedFilesSource)
                } catch (mergeErr) {
                  console.warn('[WebSocket] Falha ao mesclar arquivos com cache temporário', mergeErr)
                }
              }

              if (chatStore.chatLoadingMode && chatStore.chatLoadingMode !== 'legacy') {
                const scope = mediaScope()
                files = mergedFilesSource.map(metadataFile)
                if (chatStore.chatLoadingMode === 'paginated' && String(chatStore.activeRoomId) === String(roomId)) {
                  setTimeout(
                    () =>
                      mergedFilesSource!.forEach(async (arquivo) => {
                        const resolved = await resolveChatFile(roomId, arquivo)
                        if (scope === mediaScope() && String(chatStore.activeRoomId) === String(roomId)) {
                          chatStore.updateReferencedFile(roomId, messageId, arquivo.ref, resolved)
                        }
                      }),
                    0,
                  )
                }
              } else {
                // ✅ FASE 1: Processamento não-bloqueante com callback
                // Primeiro, adiciona metadados dos arquivos (agora usando mergedFilesSource) para mostrar imediatamente na UI
                files = mergedFilesSource.map((arquivo: any) => {
                  const directUrl = resolveDirectMediaUrl(arquivo)
                  const hasDirect = Boolean(directUrl)
                  const type = arquivo.type || 'application/octet-stream'
                  return {
                    id: arquivo.id,
                    name: arquivo.name || 'Arquivo',
                    size: arquivo.size || 0,
                    type,
                    audio: type && String(type).startsWith('audio/'),
                    url: directUrl, // Usa URL direta se existir (ou o url reaplicado do cache)
                    local: false,
                    loading: !hasDirect, // Baixando somente quando não tem URL direta
                    needsDownload: !hasDirect,
                    progress: hasDirect ? 100 : 0,
                    uploaded: Boolean(hasDirect),
                    // ✅ PRESERVAR: Preview/thumbnail se disponível (pode vir do servidor ou do cache)
                    preview: arquivo.thumb_url || arquivo.thumbnail_url || arquivo.preview || undefined,
                  }
                })

                // ✅ Processa downloads em background usando requestIdleCallback
                const processFilesInBackground = () => {
                  const messageId = data.message._id || data.message.id || data.message.temporary_id

                  mergedFilesSource!.forEach(async (arquivo: any, index: number) => {
                    try {
                      if (!arquivo || !arquivo.id) {
                        console.warn('[WebSocket] Arquivo inválido:', arquivo)
                        return
                      }

                      const directUrl = resolveDirectMediaUrl(arquivo)
                      if (directUrl) {
                        chatStore.updateMessageFile(messageId, index, {
                          loading: false,
                          progress: 100,
                          uploaded: true,
                          needsDownload: false,
                          url: directUrl,
                          preview: arquivo.thumb_url || arquivo.thumbnail_url || arquivo.preview,
                        })
                        return
                      }

                      const blob = await downloadMediaWithCache(arquivo.id)

                      if (!blob) {
                        // Marcar como erro na UI
                        chatStore.updateMessageFile(messageId, index, {
                          loading: false,
                          error: true,
                          progress: 0,
                        })
                        return
                      }

                      const isImage = blob.type.startsWith('image/')
                      let finalUrl = URL.createObjectURL(blob)
                      let imgBase64 = ''

                      // ✅ ESTRATÉGIA INTELIGENTE: Para imagens pequenas (<500KB), usar base64
                      if (isImage && blob.size < 500 * 1024) {
                        try {
                          imgBase64 = await blobToBase64(blob)
                          finalUrl = imgBase64
                        } catch (err) {
                          console.warn('[WebSocket] Falha na conversão base64:', err)
                        }
                      }

                      const extFromName =
                        arquivo.name && arquivo.name.includes('.') ? arquivo.name.split('.').pop() : ''
                      const resolvedExt = extFromName || mimeToExtension(blob.type) || 'bin'

                      // ✅ Atualizar arquivo específico na mensagem
                      chatStore.updateMessageFile(messageId, index, {
                        size: blob.size,
                        type: blob.type,
                        url: finalUrl,
                        objectUrl: isImage ? undefined : finalUrl,
                        preview: isImage ? finalUrl : undefined,
                        loading: false,
                        extension: resolvedExt,
                        progress: 100,
                        uploaded: true,
                        needsDownload: false,
                        error: false,
                        // ✅ MARCAR: Agora é a versão definitiva
                        definitive: true,
                      })

                      activeObjectUrls.add(finalUrl)
                    } catch (err) {
                      console.error('[WebSocket] Erro ao processar arquivo:', arquivo.id, err)
                      chatStore.updateMessageFile(messageId, index, {
                        loading: false,
                        error: true,
                        progress: 0,
                      })
                    }
                  })
                }

                // ✅ Agenda processamento em background
                if (typeof requestIdleCallback !== 'undefined') {
                  requestIdleCallback(processFilesInBackground, { timeout: 1000 })
                } else {
                  // Fallback para navegadores sem requestIdleCallback
                  setTimeout(processFilesInBackground, 50)
                }
              }
            }

            const resolvedExternalId = resolveExternalId(data.message)
            const isHistoricalMessage = Boolean((data.message as any).is_historical)

            // Normalize message for vue-advanced-chat compatibility
            // CORRIGIDO: Garantir timestamp válido
            // Use ISO as fallback to avoid locale-dependent parsing issues
            const timestamp = data.message.external_created_at || data.message.created_at || new Date().toISOString()

            const normalizedMessage = {
              // Required fields for vue-advanced-chat
              _id: messageId,
              id: messageId,
              content: data.message.content || '', // ✅ VALIDADO: Suporta mensagens com anexo mas sem texto

              // Optional but important fields
              createdAt: timestamp,

              // Status fields
              failure: data.message.failure || false,
              seen: data.message.seen || false,
              distributed: data.message.distributed || false,

              // Additional fields (preserve original data)
              ...data.message,

              // A preparação dos anexos prevalece sobre as URLs brutas do servidor.
              files,

              // Ensure these are set correctly
              temporary_id: data.message.temporary_id,
              senderId: senderId,
              external_id: resolvedExternalId || data.message.external_id,
              external_status: data.message.external_status,
              is_historical: isHistoricalMessage,
            }

            console.debug('[WebSocket] Normalized message:', normalizedMessage)
            console.debug('[WebSocket] Adding to room:', roomId)

            chatStore.addMessage(roomId, normalizedMessage)

            const isOutgoing = normalizedMessage.direction === 'outgoing'
            const activeRoomKey = chatStore.activeRoomId ? String(chatStore.activeRoomId) : null
            const normalizedRoomIdentifier = normalizeRoomIdentifier(roomId)
            const isHistorical = normalizedMessage.is_historical === true

            if (!isOutgoing) {
              const isActiveRoom = activeRoomKey === roomId

              if (isHistorical) {
                chatStore.updateUnreadCountForRoom(normalizedRoomIdentifier, 0)
              } else if (isActiveRoom) {
                chatStore.updateUnreadCountForRoom(normalizedRoomIdentifier, 0)
                queueMarkMessagesAsRead(roomId)

                // Emitir recibo de leitura explícito apenas com WAM ID válido
                try {
                  const wamid = extractWamid(data.message)
                  if (wamid && !sentReadReceipts[wamid]) {
                    const leadIdNumeric = Number(roomId)
                    const leadField = Number.isNaN(leadIdNumeric) ? roomId : leadIdNumeric
                    const roomKey = `chat_lead_${roomId}`
                    const payload = {
                      type: 'read_receipt',
                      lead: leadField,
                      message_id: wamid,
                      channel: normalizedMessage.channel || (data.message as any).channel,
                    }
                    if (sendMessageToRoom(roomKey, payload)) {
                      sentReadReceipts[wamid] = Date.now()
                      cleanupSentReadReceipts()
                    }
                  }
                } catch (err) {
                  console.warn('[WebSocket] Falha ao enviar read_receipt para mensagem recebida', err)
                }
              } else {
                chatStore.updateUnreadCountForRoom(normalizedRoomIdentifier, 1)
              }

              if (!isHistorical) {
                lastUnreadUpdateFromChat[roomId] = Date.now()
              }
            }

            // ✅ NOVO: Confirmar recebimento da mensagem no tracking system
            if (normalizedMessage.temporary_id) {
              // Agendar limpeza do cache após preview ser substituído
              setTimeout(() => {
                if (temporaryFileCache.has(normalizedMessage.temporary_id)) {
                  const cached = temporaryFileCache.get(normalizedMessage.temporary_id) || []
                  // Limpar URLs blob temporárias antes de remover do cache
                  cached.forEach((file: any) => {
                    if (file.url && file.url.startsWith('blob:')) {
                      try {
                        URL.revokeObjectURL(file.url)
                      } catch (e) {
                        // Ignorar erros de revogação
                      }
                    }
                  })
                  temporaryFileCache.delete(normalizedMessage.temporary_id)
                  console.log('[WebSocket] Cache temporário limpo para:', normalizedMessage.temporary_id)
                }
              }, 5000) // 5 segundos para permitir que o usuário veja o preview

              EventBus.emit('messageConfirmed', normalizedMessage.temporary_id)
            }

            // Atualiza o último horário de comunicação (qualquer direção)
            chatStore.updateLastCommunication(roomId, normalizedMessage.timestamp || Date.now())
            // Atualiza o último horário de comunicação recebida (regra WhatsApp)
            if (normalizedMessage.direction !== 'outgoing') {
              chatStore.updateLastIncoming(roomId, normalizedMessage.timestamp || Date.now())
            }

            // Force reactivity trigger if needed
            console.debug('[WebSocket] Current active room:', chatStore.activeRoomId)
            console.debug('[WebSocket] Messages in store:', chatStore.messagesForRoom(roomId).length)
          }
        }
        break

      case 'status_update': {
        // Backend event to update external delivery/read status for a message
        const roomIdRaw = data.room_id ?? data.roomId ?? data.lead_id ?? data.lead
        const messageId = data.id ?? data.message_id ?? data.communication_id
        const temporaryId = data.temporary_id ?? data.temporaryId ?? null
        const externalStatus = data.external_status ?? data.status ?? data.state
        const externalId = resolveExternalId(data)

        if (!roomIdRaw || (!messageId && !temporaryId && !externalId) || !externalStatus) {
          console.warn('[WebSocket] status_update incompleto', {
            hasRoom: Boolean(roomIdRaw),
            hasId: Boolean(messageId),
            hasTemporary: Boolean(temporaryId),
            hasExternalId: Boolean(externalId),
            hasStatus: Boolean(externalStatus),
            payload: data,
          })
          break
        }

        const roomId = String(roomIdRaw)
        const updateData = {
          roomId,
          messageId,
          temporaryId,
          externalStatus,
          externalId,
        }

        // If the message is not present yet (e.g., WhatsApp App echo), request a room refresh
        const existing = (() => {
          try {
            const list = chatStore.messagesForRoom(roomId) || []
            return list.some((m: any) => String(m._id) === String(messageId) || String(m.id) === String(messageId))
          } catch {
            return false
          }
        })()

        chatStore.updateExternalStatus(updateData)

        if (!existing) {
          const now = Date.now()
          const last = roomRefreshThrottle[roomId] || 0
          if (now - last > 800) {
            roomRefreshThrottle[roomId] = now
            EventBus.emit('chat:refresh-room', { roomId })
          }
        }
        break
      }

      case 'typing':
        handleTypingIndicator(chatStore, data)
        break

      case 'send_notification': {
        const notificationRoomId = data.lead_id || data.roomId

        const rawCommType =
          data.comm_type || data.commType || data.notification_type || data.notificationType || data.category || ''
        const normalizedCommType = typeof rawCommType === 'string' ? rawCommType.toLowerCase() : ''
        const isStatusLike = normalizedCommType === 'status' || normalizedCommType === 'reaction'
        const isHistoricalNotification = Boolean(data.is_historical)

        if (!notificationRoomId || isStatusLike || isHistoricalNotification) {
          break
        }

        const roomKey = String(notificationRoomId)
        const activeRoomKey = chatStore.activeRoomId ? String(chatStore.activeRoomId) : null
        const lastChatUpdate = lastUnreadUpdateFromChat[roomKey] || 0
        const shouldFallback = Date.now() - lastChatUpdate > NOTIFICATION_FALLBACK_WINDOW_MS

        if (activeRoomKey !== roomKey && shouldFallback) {
          chatStore.updateUnreadCountForRoom(normalizeRoomIdentifier(roomKey), 1)
          console.log('[WebSocket] Notificação incrementou unread (fallback):', {
            roomId: notificationRoomId,
            message: data.message,
          })
        }

        EventBus.emit('notification', {
          roomId: notificationRoomId,
          message: data.message,
          commType: normalizedCommType || 'message',
          isHistorical: isHistoricalNotification,
          raw: data,
        })
        break
      }

      case 'room_update': {
        const { room, action } = data

        if (!room || typeof room !== 'object') {
          console.warn('[WebSocket] room_update sem room válido', data)
          break
        }

        const normalizedAction = typeof action === 'string' ? action.trim().toLowerCase() : 'updated'
        const rawRoomId = room.id ?? room.roomId ?? room.room_id

        if (['delete', 'deleted', 'remove', 'removed', 'closed'].includes(normalizedAction)) {
          if (rawRoomId !== undefined && rawRoomId !== null) {
            chatStore.removeRoom(rawRoomId)
            console.log('[WebSocket] room_update: Sala removida', { roomId: rawRoomId, action: normalizedAction })
          } else {
            console.warn('[WebSocket] room_update: ação de remoção sem roomId', { action: normalizedAction, room })
          }
          break
        }

        if (['create', 'created'].includes(normalizedAction)) {
          chatStore.addRoom(room)
          break
        }

        chatStore.upsertRoom(room)
        break
      }
      default:
        console.warn('[WebSocket] Mensagem não reconhecida:', data)
    }
  }

  socket.onerror = (error) => {
    console.error(`[WebSocket] ❌ Erro: "${key}":`, error)
  }

  socket.onclose = () => {
    console.log(`[WebSocket] 🔴 Fechado: ${key}`)
    delete sockets[key]

    const pendingTimeout = reconnectTimeouts[key]
    if (pendingTimeout) {
      clearTimeout(pendingTimeout)
      delete reconnectTimeouts[key]
    }

    if (manualDisconnects.has(key)) {
      manualDisconnects.delete(key)
      delete reconnectAttempts[key]
      return
    }

    // Lógica de reconexão com backoff exponencial melhorado
    attempts++
    reconnectAttempts[key] = attempts

    const maxReconnectAttempts = 10 // Limite máximo de tentativas
    if (attempts > maxReconnectAttempts) {
      console.error(`[WebSocket] 🚫 Máximo de tentativas atingido para "${key}". Parando.`)
      delete reconnectAttempts[key]
      return
    }

    // const delay = Math.min(30000, 1000 * Math.pow(2, attempts)) // Máx 30s

    // console.log(
    //   `[WebSocket] Tentando reconectar "${key}" em ${delay}ms (tentativa ${attempts}/${maxReconnectAttempts})...`,
    // )

    // ✅ Backoff com jitter para evitar thundering herd
    const baseDelay = 1000 * Math.pow(2, Math.min(attempts, 6)) // Max 64s base
    const jitter = Math.random() * 1000 // 0-1s de aleatoriedade
    const delay = Math.min(30000, baseDelay + jitter) // Máx 30s total

    console.log(
      `[WebSocket] 🔄 Reconectando "${key}" em ${Math.round(delay)}ms (${attempts}/${maxReconnectAttempts})...`,
    )

    reconnectTimeouts[key] = setTimeout(() => {
      delete reconnectTimeouts[key]
      connectWebSocket(url, key)
    }, delay)
  }

  sockets[key] = socket // Armazena o WebSocket no gerenciador
  return socket
}

// ✅ Função auxiliar para descrição do estado
function getStateDescription(state: number): string {
  const states: Record<number, string> = {
    [WebSocket.CONNECTING]: 'conectando',
    [WebSocket.OPEN]: 'conectado',
    [WebSocket.CLOSING]: 'fechando',
    [WebSocket.CLOSED]: 'fechado',
  }
  return states[state] || 'desconhecido'
}

// ✅ NOVO: Limpeza periódica do cache do WebSocket (agora gerenciada pelo CacheManager)
// O CacheManager já faz a limpeza automática, então este código não é mais necessário

export const sendMessageToRoom = (roomKey: string, message: any) => {
  const websocket = sockets[roomKey]

  if (!websocket) {
    console.error(`WebSocket para a sala "${roomKey}" não existe.`)
    return false
  }

  if (websocket.readyState === WebSocket.OPEN) {
    try {
      websocket.send(JSON.stringify(message))
      console.log(`Mensagem enviada para a sala "${roomKey}":`, message)
      return true
    } catch (error) {
      console.error(`Erro ao enviar mensagem para a sala "${roomKey}":`, error)
      return false
    }
  } else {
    const states: Record<number, string> = {
      [WebSocket.CONNECTING]: 'conectando',
      [WebSocket.CLOSING]: 'fechando',
      [WebSocket.CLOSED]: 'fechado',
    }
    console.error(
      `WebSocket para a sala "${roomKey}" não está conectado. Estado: ${
        states[websocket.readyState] || 'desconhecido'
      }`,
    )
    return false
  }
}

export function disconnectWebSocket(key: string) {
  const socket = sockets[key]
  if (socket) {
    manualDisconnects.add(key)

    const pendingTimeout = reconnectTimeouts[key]
    if (pendingTimeout) {
      clearTimeout(pendingTimeout)
      delete reconnectTimeouts[key]
    }

    socket.close()
    delete sockets[key] // Remove a conexão do gerenciador após fechar
    console.log(`[WebSocket] 🔌 Desconectado: "${key}"`)
  } else {
    // ✅ Reduzir para debug - pode ser situação normal durante limpeza
    console.debug(`[WebSocket] 🔍 Conexão "${key}" não encontrada (já pode ter sido limpa)`)
  }
}

export function disconnectAllWebSockets() {
  Object.keys(sockets).forEach((key) => disconnectWebSocket(key))
  console.log('Todas as conexões WebSocket foram encerradas.')
}

/** Clears module-level caches to prevent data leakage on company switch */
export function clearWebSocketCaches(): void {
  // Revoke blob URLs to free memory
  activeObjectUrls.forEach((url) => {
    try {
      URL.revokeObjectURL(url)
    } catch {
      // ignore
    }
  })
  activeObjectUrls.clear()

  temporaryFileCache.clear()

  // Cancel typing cleanup timers
  Object.keys(typingCleanupTimers).forEach((key) => {
    clearTimeout(typingCleanupTimers[key])
    delete typingCleanupTimers[key]
  })

  // Clear throttle and tracking maps
  Object.keys(roomRefreshThrottle).forEach((key) => delete roomRefreshThrottle[key])
  Object.keys(lastUnreadUpdateFromChat).forEach((key) => delete lastUnreadUpdateFromChat[key])
  Object.keys(readReceiptThrottleMap).forEach((key) => delete readReceiptThrottleMap[key])
  Object.keys(sentReadReceipts).forEach((key) => delete sentReadReceipts[key])

  console.log('[WebSocket] Module-level caches cleared')
}

// Função utilitária para verificar se um WebSocket está conectado
export function isWebSocketConnected(key: string): boolean {
  const socket = sockets[key]
  return socket && socket.readyState === WebSocket.OPEN
}

// 🆕 Função para verificar saúde da conexão
export function isWebSocketHealthy(key: string): boolean {
  const socket = sockets[key]
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    return false
  }

  const lastActivity = (socket as any)._lastActivity || (socket as any)._connectedAt || Date.now()
  const timeSinceActivity = Date.now() - lastActivity

  // Se passou mais de 5 minutos sem atividade, consideramos não saudável
  return timeSinceActivity < 300000 // 5 minutos
}

// ✅ Função para forçar limpeza de conexões órfãs
export function cleanupOrphanedConnections(): void {
  const orphaned: string[] = []

  Object.keys(sockets).forEach((key) => {
    const socket = sockets[key]
    if (!socket || socket.readyState === WebSocket.CLOSED) {
      orphaned.push(key)
    }
  })

  orphaned.forEach((key) => {
    console.log(`[WebSocket] 🧹 Limpando conexão órfã: ${key}`)
    delete sockets[key]
    delete reconnectAttempts[key]
  })

  if (orphaned.length > 0) {
    console.log(`[WebSocket] ✅ Limpou ${orphaned.length} conexões órfãs`)
  }
}

// Função utilitária para obter o estado de um WebSocket
export function getWebSocketState(key: string): string {
  const socket = sockets[key]
  if (!socket) return 'não existe'

  const states: Record<number, string> = {
    [WebSocket.CONNECTING]: 'conectando',
    [WebSocket.OPEN]: 'conectado',
    [WebSocket.CLOSING]: 'fechando',
    [WebSocket.CLOSED]: 'fechado',
  }

  return states[socket.readyState] || 'desconhecido'
}

// Função utilitária para listar todas as conexões ativas
export function getActiveWebSockets(): string[] {
  return Object.keys(sockets).filter((key) => isWebSocketConnected(key))
}

// Função utilitária para obter estatísticas das conexões
export function getWebSocketStats(): Record<string, any> {
  const stats = {
    total: Object.keys(sockets).length,
    connected: 0,
    connecting: 0,
    closing: 0,
    closed: 0,
    connections: {} as Record<string, string>,
  }

  Object.keys(sockets).forEach((key) => {
    const state = getWebSocketState(key)
    stats.connections[key] = state

    switch (state) {
      case 'conectado':
        stats.connected++
        break
      case 'conectando':
        stats.connecting++
        break
      case 'fechando':
        stats.closing++
        break
      case 'fechado':
        stats.closed++
        break
    }
  })

  return stats
}

// Função para converter Blob em Base64
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('Falha ao converter Blob para Base64.'))
      }
    }

    reader.onerror = () => reject(reader.error)

    reader.readAsDataURL(blob)
  })
}

export async function downloadMediaWithToken(fileId: string): Promise<Blob> {
  // Use central http instance so interceptors (refresh token, baseURL) are applied
  const response = await http.get(`/integrations/media/${fileId}/`, {
    responseType: 'blob',
  })

  return response.data
}

// ✅ NOVO: Cache compartilhado entre WebSocket e componentes
const downloadQueue = new Set()
const maxConcurrentDownloads = 3

// ✅ OTIMIZADO: Download com cache centralizado e controle de concorrência
async function downloadMediaWithCache(fileId: string): Promise<Blob | null> {
  // Verificar cache centralizado primeiro
  const cached = CacheManager.get<{ blob: Blob; size: number }>('websocket-files', fileId)
  if (cached) {
    console.log(`[WEBSOCKET] Usando arquivo do cache: ${fileId}`)
    return cached.blob
  }

  // Verificar se já está baixando
  if (downloadQueue.has(fileId)) {
    // Aguardar download em andamento
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        const cachedRetry = CacheManager.get<{ blob: Blob; size: number }>('websocket-files', fileId)
        if (cachedRetry) {
          clearInterval(checkInterval)
          resolve(cachedRetry.blob)
        } else if (!downloadQueue.has(fileId)) {
          clearInterval(checkInterval)
          resolve(null) // Download falhou
        }
      }, 100)
    })
  }

  // Controlar concorrência
  if (downloadQueue.size >= maxConcurrentDownloads) {
    console.log(`[WEBSOCKET] Limite de downloads atingido, aguardando: ${fileId}`)
    return null // Será baixado depois
  }

  downloadQueue.add(fileId)

  try {
    console.log(`[WEBSOCKET] Iniciando download: ${fileId}`)
    const blob = await downloadMediaWithToken(fileId)

    if (blob && blob.size > 0 && !blob.type.includes('text/html')) {
      // ✅ Salvar no cache centralizado
      CacheManager.set(
        'websocket-files',
        fileId,
        {
          blob,
          size: blob.size,
        },
        CacheManager.WEBSOCKET_CACHE_TTL,
      )

      console.log(`[WEBSOCKET] Download concluído e cacheado: ${fileId} (${blob.size} bytes)`)
      return blob
    }

    return null
  } catch (error) {
    console.error(`[WEBSOCKET] Erro no download: ${fileId}`, error)
    return null
  } finally {
    downloadQueue.delete(fileId)
  }
}
function normalizeRoomIdentifier(rawRoomId: string): string | number {
  if (rawRoomId === undefined || rawRoomId === null) {
    return ''
  }

  const numeric = Number(rawRoomId)
  return Number.isNaN(numeric) ? rawRoomId : numeric
}

function queueMarkMessagesAsRead(roomId: string) {
  const key = String(roomId)
  const now = Date.now()
  const lastCall = readReceiptThrottleMap[key] || 0

  if (now - lastCall < READ_RECEIPT_THROTTLE_MS) {
    return
  }

  readReceiptThrottleMap[key] = now

  markMessagesAsReadAsync(key)
}

async function markMessagesAsReadAsync(key: string) {
  try {
    const leadIdentifier = normalizeRoomIdentifier(key)
    await http.post('/lead/readMessages/', { lead_id: leadIdentifier })
    console.debug('[WebSocket] Mensagens marcadas como lidas', { roomId: leadIdentifier })
  } catch (error) {
    console.warn('[WebSocket] Falha ao marcar mensagens como lidas', { roomId: key, error })
  }
}
